/**
 * Game state
 */
// ==================== Import ====================
import Board from '../objects/Board'
import BombPowerup from '../objects/BombPowerup'
import LandscapeGameUI from '../gameui/LandscapeGameUI'
import PortraitGameUI from '../gameui/PortraitGameUI'
import OverlayScreens from '../ui/OverlayScreens'

export default class Game extends Phaser.State {

    // ==================== Construction ====================
    create() {
        var stats = new Stats();
        stats.setMode(0); // 0: fps, 1: ms
        stats.domElement.style.position = 'absolute';
        stats.domElement.style.left = '0px';
        stats.domElement.style.top = '0px';

        // uncomment this to see FPS display
        // document.body.appendChild(stats.domElement)
        this.stats = stats;
        window.GS = this;

        // Create background
        this.bg = PARAMS.addSprite(this, 0, 0, 'preloader', 'preloadBg.png');
        this.bg.anchor.set(0.5);

        // Create view container
        this.view = this.add.group();
        this.gameView = this.add.group();
        this.gameView.add(this.bg);
        this.gameView.add(this.view);

        // Create blur filter
        this.blurX = this.add.filter('BlurX');
        this.blurY = this.add.filter('BlurY');
        this.blurX.blur = 75;
        this.blurY.blur = 75;
        this.gameView.filters = [this.blurX, this.blurY];

        // Create obejcts in the game
        this.board = new Board(this.game, this);
        this.bomb = new BombPowerup(this.game, this.board);
        this.landscapeUI = new LandscapeGameUI(this);
        this.portraitUI = new PortraitGameUI(this);
        this.overlayScreens = new OverlayScreens(this);

        // Add objects to the view
        this.view.add(this.board.view);
        this.view.add(this.landscapeUI.view);
        this.view.add(this.portraitUI.view);
        this.view.add(this.bomb.view);

        // Initialize variables
        this.focused = true;
        this.fakePaused = false; // indicates if game is paused by lost of focus
        this.pausedByArkadium = false;

        // On desktop, set focus and blur callbacks
        if (GS.game.device.desktop) {
            this.game.onFocus.add(this.onFocus);
            this.game.onBlur.add(this.onBlur);
            this.game.canvas.addEventListener('focus', this.onFocus, true);
            this.game.canvas.addEventListener('blur', this.onBlur, true);
            this.game.canvas.addEventListener('click', function () {
                this.game.canvas.focus();
            }.bind(this), true);
        }
        // On mobile, only enable muting/unmuting on focus events
        else {
            this.game.onFocus.add(this.unsetMute);
            this.game.onBlur.add(this.setMute);
            this.game.canvas.addEventListener('focus', this.unsetMute, true);
            this.game.canvas.addEventListener('blur', this.setMute, true);

            if ("hidden" in document) {
                document.addEventListener("visibilitychange", this.onVisibilityChanged);
            }
            else if ("mozHidden" in document) {
                document.addEventListener("mozvisibilitychange", this.onVisibilityChanged);
            }
            else if ("webkitHidden" in document) {
                document.addEventListener("webkitvisibilitychange", this.onVisibilityChanged);
            }
            else if ("msHidden" in document) {
                document.addEventListener("msvisibilitychange", this.onVisibilityChanged);
            }
            else if ("onfocusin" in document) { // IE9 and lower
                document.onfocusin = document.onfocusout = this.onVisibilityChanged;
            }
            else { // All others
                window.onpageshow = window.onpagehide = window.onfocus = window.onblur = this.onVisibilityChanged;
            }
        }

        // Register Arkadium Arena events
        ARK_game_arena_connector.registerAction('pause', this.pauseGame.bind(this));
        ARK_game_arena_connector.registerAction('resume', this.resumeGame.bind(this));

        // Start the first level
        this.setupLevel(1);
    }

    // ==================== Focusing ====================
    // Focus
    onFocus() {
        if (GS.focused) { return; }
        GS.focused = true;
        GS.game.sound.unsetMute();

        if (GS.fakePaused) {
            ARK_game_arena_connector.fireEventToArena('event_change');
            GS.overlayScreens.hideScreen();
            GS.fakePaused = false
        }
    };

    // Blur
    onBlur() {
        if (!GS.focused) { return; }
        GS.focused = false;
        GS.game.sound.setMute();

        if (!GS.paused) {
            ARK_game_arena_connector.fireEventToArena('event_change');
            GS.pause();
            GS.overlayScreens.showScreen('fakePause');
            GS.fakePaused = true
        }
    };

    // Visibility changes to track focus changes on mobile
    onVisibilityChanged(event) {
        // Check if document supports visibility state
        if (document.visibilityState) {
            if (document.visibilityState === "visible") { GS.unsetMute(); }
            else if (document.visibilityState === "hidden") { GS.setMute(); }
        }
        // Otherwise, check the event type for visibility state
        else {
            var v = "visible", h = "hidden", eventMap = { focus:v, focusin:v, pageshow:v, blur:h, focusout:h, pagehide:h };
            event = event || window.event;
            if (event.type in eventMap) {
                if (eventMap[event.type] === v) { GS.unsetMute(); }
                else { GS.setMute(); }
            }
        }
    }

    // ==================== Levels ====================
    setupLevel(level) {
        STATS.updateHighestLevel(level);

        // Clean up any objects from previous levels
        this.game.tweens.removeAll();
        // this.view.removeAll(true);

        // Get the time left
        var times = PARAMS.timesPerLevel;
        if (level > times.length) {
            this.timeLeft = 60 * 1000;
        } else {
            this.timeLeft = times[level - 1] * 1000;
        }
        this.timeOriginal = this.timeLeft;
        this.timeActive = false;
        this.timeLast = this.timeLeft;
        this.time.events.add(1000, function () {
            this.timeActive = true
        }, this);

        // Reset the variables
        this.bestWordScoreLevel = 0;
        this.bestWordLevel = '';
        this.goalReached = false;
        this.goal = Math.min(15, level);
        this.level = level;
        this.levelScore = 0;
        this.lines = 0;

        // If this is the first level, then set all variables to the starting values
        if (level === 1) {
            this.score = 0;
            this.bestWordScore = 0;
            this.bestWord = '';
            this.availableBombs = 0;
            this.bombCharger = 0;
        }

        // Initialize all objects
        this.board.init(level);
        this.landscapeUI.init(this);
        this.portraitUI.init(this);

        // Reset the bombs
        if (this.availableBombs > 0) { this.bomb.activate(); }
        this.landscapeUI.updateFiller(this.bombCharger / 500);

        // Initialize state
        this.pause();
        this.resize(window.innerWidth, window.innerHeight);
        this.updateAllMuteButtons();
    }

    setupNextLevel() {
        GS.setupLevel(GS.level + 1);
    }

    isFresh() {
        return this.timeOriginal === this.timeLeft;
    }

    // ==================== Game Flow ====================
    startGame() {
        this.resume();
    }

    pauseGame() {
        this.pausedByArkadium = true;
        AUDIO.updateVolume();
        this.portraitUI.clearCurrentScore();
        this.landscapeUI.clearCurrentScore();

        this.board.onPause();
        this.bomb.onPause();

        this.landscapeUI.onPause();
        this.portraitUI.onPause();
    }

    resumeGame() {
        this.pausedByArkadium = false;
        AUDIO.updateVolume();
        if (!this.paused) this.resume();
    }

    pause() {
        if (!this.isFresh) ARK_game_arena_connector.fireEventToArena('event_change');

        this.portraitUI.clearCurrentScore();
        this.landscapeUI.clearCurrentScore();

        this.paused = true;
        this.board.onPause();
        this.bomb.onPause();

        this.landscapeUI.onPause();
        this.portraitUI.onPause();
    }

    resume() {
        this.paused = false;
        this.board.onResume();
        this.bomb.onResume();

        this.landscapeUI.onResume();
        this.portraitUI.onResume();
    }

    // ==================== Update ====================
    update() {
        var blur = Phaser.Math.mapLinear(this.overlayScreens.view.alpha, 0, 1, 0, 75);
        this.blurX.blur = blur;
        this.blurY.blur = blur;
        if (this.pausedByArkadium) return;

        var dt = this.game.time.elapsed;
        if (PARAMS.frameBasedTimer) {
            dt = Math.max(1000 / 60, this.game.time.elapsed * 0.3);
        }

        this.stats.begin();

        if (this.timeActive && !this.paused && this.focused) {
            this.timeLeft -= dt;

            if (this.timeLeft < 0) {
                this.timeActive = false;
                this.timeLeft = 0;
                this.onTimeOver()
            }
        }

        var leftInSeconds = Math.round(this.timeLeft / 1000);
        if (leftInSeconds !== this.timeLast && leftInSeconds === 30) AUDIO.playSound('time_30sec');
        else if (leftInSeconds !== this.timeLast && leftInSeconds <= 15)AUDIO.playSound('time_15sec');

        this.timeLast = leftInSeconds;

        this.landscapeUI.updateTimer(this.timeLeft);
        this.portraitUI.updateTimer(this.timeLeft);
        // this.topPanel.setTimer(Math.round(this.timeLeft / 1000))
    }

    render() {
        this.stats.end();
        // //console.log("render!")
    }

    // ==================== Resizing ====================
    resize(w, h) {
        var scalePortrait = Math.min(w / 518, h / 778);
        var scaleLandscape = Math.min(w / 640, h / 480);

        if (scaleLandscape > scalePortrait) {
            // landscape
            this.view.pivot.set(320 * PARAMS.scaleFactor, 240 * PARAMS.scaleFactor);
            this.landscapeUI.show();
            this.portraitUI.hide();

            this.board.view.x = (640 - 477) * PARAMS.scaleFactor;
            this.board.view.y = 4 * PARAMS.scaleFactor;

            this.view.x = this.bg.x = w / 2;
            this.view.y = this.bg.y = h / 2;

            var scale = Math.min(w / 640 / PARAMS.scaleFactor, h / 480 / PARAMS.scaleFactor);
            this.view.scale.set(scale);

            var bgScale = Math.max(w / 640 / PARAMS.scaleFactor, h / 480 / PARAMS.scaleFactor);
            this.bg.scale.set(bgScale * PARAMS.scaleFactor);

            this.bomb.setBasePosition(39 * PARAMS.scaleFactor, 286 * PARAMS.scaleFactor);
            this.bomb.view.scale.set(0.88);

            this.overlayScreens.setOrientation('landscape');
        }
        else {
            // portrait
            this.view.pivot.set(518 / 2 * PARAMS.scaleFactor, 778 / 2 * PARAMS.scaleFactor);
            this.landscapeUI.hide();
            this.portraitUI.show();

            this.board.view.x = 24 * PARAMS.scaleFactor;
            this.board.view.y = 154 * PARAMS.scaleFactor;

            this.view.x = this.bg.x = w / 2;
            this.view.y = this.bg.y = h / 2;

            var scale = Math.min(w / 518 / PARAMS.scaleFactor, h / 778 / PARAMS.scaleFactor);
            this.view.scale.set(scale);

            var bgScale = Math.max(w / 640 / PARAMS.scaleFactor, h / 480 / PARAMS.scaleFactor);
            this.bg.scale.set(bgScale * PARAMS.scaleFactor);

            this.bomb.setBasePosition(223 * PARAMS.scaleFactor, 27 * PARAMS.scaleFactor);
            this.bomb.view.scale.set(1);

            this.overlayScreens.setOrientation('portrait')
        }

        this.overlayScreens.view.x = this.view.x;
        this.overlayScreens.view.y = this.view.y;
        if (!GS.game.device.webGL || GS.game.renderType === 1) {
            this.overlayScreens.blurredBg.scale.set(w / 640 * PARAMS.scaleFactor / this.view.scale.x,
                h / 480 * PARAMS.scaleFactor / this.view.scale.x);
        }
        this.overlayScreens.view.scale.set(this.view.scale.x);

        this.landscapeUI.updateTexts();
        this.portraitUI.updateTexts()
    }

    // ==================== Events ====================
    onGoalReached() {
        if (this.goalReached) { return; }
        this.goalReached = true;
        this.landscapeUI.goalReached();
        this.portraitUI.goalReached()
    }

    onTimeOver() {
        this.board.boardInputController.active = false;
        this.pause();
        this.time.events.add(1200, function () {
            this.overlayScreens.showScreen('gameSummary');
        }, this);
    }

    newLineCleared() {
        STATS.onLineCleared();

        this.lines += 1;
        if (this.lines >= this.goal) {
            this.onGoalReached()
        }

        this.landscapeUI.updateGoals(this.lines, this.goal);
        this.portraitUI.updateGoals(this.lines, this.goal);
        // this.bottomPanel.setGoals(this.lines, this.goal);

        if (this.lines === 20) {
            this.pause();
            this.time.events.add(1400, function () {
                // this.setupLevel(this.level + 1);
                this.overlayScreens.showScreen('doubleScore');
                AUDIO.playSound('success')
            }, this)
        }
    }

    onWordChanged(word, valid, points) {
        this.landscapeUI.updateSelectedWord(word, valid, points);
        this.portraitUI.updateSelectedWord(word, valid, points)
    }

    onWordConfirmed(score, word) {
        ARK_game_arena_connector.fireEventToArena('event_change');

        this.score += score;
        this.levelScore += score;

        if (score > this.bestWordScore) {
            this.bestWordScore = score;
            this.bestWord = word
        }

        if (score > this.bestWordScoreLevel) {
            this.bestWordScoreLevel = score;
            this.bestWordLevel = word
        }

        STATS.updateBestWord(word, score);
        STATS.updateHighestScore(this.score);

        this.landscapeUI.updateTotalScore(this.score);
        this.landscapeUI.clearCurrentScore();

        this.portraitUI.updateTotalScore(this.score);
        this.portraitUI.clearCurrentScore();

        if (this.goalReached) {
            this.bombCharger += score;
            var counter = 0;

            while (this.bombCharger > 500) {
                this.bombCharger -= 500;
                this.availableBombs += 1;
                this.landscapeUI.updateBombCounter(this.availableBombs);
                this.portraitUI.updateBombCounter(this.availableBombs);
                counter += 1;
            }

            if (counter >= 1 && this.availableBombs === counter) {
                this.bomb.activate();
                AUDIO.playSound('bombCharged')
            } else if (counter > 0) {
                this.bomb.playActivationAnim();
                AUDIO.playSound('bombCharged')
            }
            this.landscapeUI.updateFiller(this.bombCharger / 500);
            this.portraitUI.updateFiller(this.bombCharger / 500)
        }
    }

    onBombDroppedWithUse() {
        ARK_game_arena_connector.fireEventToArena('event_change');

        this.availableBombs -= 1;
        this.landscapeUI.updateBombCounter(this.availableBombs);
        this.portraitUI.updateBombCounter(this.availableBombs);

        this.portraitUI.updateTotalScore(this.score);
        this.portraitUI.clearCurrentScore();

        this.landscapeUI.updateTotalScore(this.score);
        this.landscapeUI.clearCurrentScore();

        if (this.availableBombs === 0) {
            // //console.log()
            this.bomb.playAnimationWithDisabling()
        } else {
            this.bomb.playActivationAnim()
        }
    }

    onBombDroppedWithoutUse() {
        if (!this.isFresh())AUDIO.playSound('bombCharged');
        this.landscapeUI.updateBombCounter(this.availableBombs);
        this.portraitUI.updateBombCounter(this.availableBombs);
        this.bomb.playActivationAnim();

        this.portraitUI.clearCurrentScore();
        this.landscapeUI.clearCurrentScore()
    }

    onBombDragged() {
        this.landscapeUI.updateBombCounter(0)
    }

    // ==================== Audio ====================
    mute() {
        AUDIO.allMuted = true;
        AUDIO.muteAll();
        this.updateAllMuteButtons();
    }

    unmute() {
        AUDIO.allMuted = false;
        AUDIO.unmuteAll();
        this.updateAllMuteButtons();
    }

    setMute() {
        console.log("setMute");
        GS.game.sound.setMute();
    }

    unsetMute() {
        console.log("unsetMute");
        GS.game.sound.unsetMute();
    }

    updateAllMuteButtons() {
        this.portraitUI.updateMuteButtons();
        this.landscapeUI.updateMuteButtons();
        this.overlayScreens.gameStart.updateMuteButtons();
    }

    // ==================== Destruction ====================
    dispose() {
        super.dispose();

        // Remove all focus/blur events
        this.game.onFocus.remove(this.onFocus);
        this.game.onBlur.remove(this.onBlur);
        this.game.canvas.removeEventListener('focus', this.onFocus);
        this.game.canvas.removeEventListener('blur', this.onBlur);

        this.game.onFocus.remove(this.unsetMute);
        this.game.onBlur.remove(this.setMute);
        this.game.canvas.removeEventListener('focus', this.unsetMute);
        this.game.canvas.removeEventListener('blur', this.setMute);

        document.removeEventListener("visibilitychange", this.onVisibilityChanged);
        document.removeEventListener("mozvisibilitychange", this.onVisibilityChanged);
        document.removeEventListener("webkitvisibilitychange", this.onVisibilityChanged);
        document.removeEventListener("msvisibilitychange", this.onVisibilityChanged);
    }
}
